import React from 'react'
import clsx from 'clsx'
import { useDrag, useDrop } from 'react-dnd'
import { GrDrag } from 'react-icons/gr'
import { Archive, ArchiveSlash, Eye, EyeSlash, Lock } from 'iconsax-react'
import { useTheme } from '@mui/material'

import Tooltip from '../../../../../components/atoms/Tooltip'

import { DD_ITEM } from '..'
import useStyles from './styles'

type SettingProps = {
  id?: string
  onClick: () => void
  onDrop?: (sourceID: string, targetID: string, isAfter: boolean) => void
  hidden?: boolean
  isCustom?: boolean
  atLimit?: boolean
  atLimitMessage?: string
  required?: boolean
  children?: React.ReactNode
}

type ItemSpec = {
  type: string
  id: string
}

const Setting: React.FC<SettingProps> = ({
  id,
  onClick,
  onDrop,
  hidden,
  isCustom,
  atLimit,
  atLimitMessage = '',
  required,
  children
}) => {
  const theme = useTheme()

  const dragRef = useDrag({
    item: {
      type: DD_ITEM,
      id
    }
  })[1]
  const [{ isOver: overTop }, topDropRef] = useDrop({
    accept: DD_ITEM,
    drop: (item: ItemSpec) => onDrop(item.id, id, true),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })
  const [{ isOver: overBottom }, botDropRef] = useDrop({
    accept: DD_ITEM,
    drop: (item: ItemSpec) => onDrop(item.id, id, false),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const classes = useStyles({ hidden })

  const VisIcon = React.useMemo(() => {
    if (required && !hidden) {
      return Lock
    }

    if (isCustom) {
      return hidden ? ArchiveSlash : Archive
    }

    return hidden ? EyeSlash : Eye
  }, [required, hidden, isCustom])

  const disableClick = React.useMemo(
    () => isCustom && hidden && atLimit,
    [isCustom, hidden, atLimit]
  )

  return (
    <div className={classes.root}>
      {!hidden && (
        <div
          ref={topDropRef}
          className={clsx(classes.dropArea, overTop && classes.dropTop)}
        />
      )}

      <Tooltip title={disableClick ? atLimitMessage : null} arrow>
        <div
          className={classes.setting}
          style={{ opacity: disableClick ? 0.5 : 1 }}
        >
          {!hidden && (
            <div ref={dragRef} className={classes.drag}>
              <GrDrag />
            </div>
          )}

          <Tooltip title={required ? 'This column cannot be hidden' : null}>
            <>
              <VisIcon
                onClick={() => {
                  if (required && !hidden) {
                    return
                  }

                  if (!disableClick) {
                    onClick()
                  }
                }}
                size={theme.size.lg}
                style={{
                  cursor: disableClick || required ? 'default' : 'pointer',
                  transform: isCustom ? 'scale(-1, 1)' : 'unset',
                  color: isCustom ? 'rgba(0, 0, 0, 0.65)' : 'black'
                }}
              />
            </>
          </Tooltip>

          <div>{children}</div>
        </div>
      </Tooltip>

      {!hidden && (
        <div
          ref={botDropRef}
          className={clsx(classes.dropArea, overBottom && classes.dropBot)}
        />
      )}
    </div>
  )
}

export default Setting
